import { Link, SubmitOptions, useFetcher, useParams } from "react-router-dom";
import { useScriptNameLoader } from "loaders/scripts.loader";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Radio,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import theme from "theme";
import { ChangeEvent, DragEvent, MouseEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router";
import { P } from "theme/typography";
import CopyToClipboard from "components/CopyToClipboard";
import {
  CaseClientStatus,
  CaseReviewStatus,
  KeyValue,
  LensScriptFlag,
  OutcomeStatus,
  RunScriptDocument,
  RunScriptMutationVariables,
  ScriptResultView,
  type SearchCasesResult,
} from "generated/graphql";
import { displayDateAndTime, displayOutcomeStatus, formatDateBackend, INTERNAL_USER_NAME, parseSafeInt } from "Backend";
import { runGqlMutation } from "gqlClient";
import { useDecoratedFetcher, useFetcherWithSnackbar } from "routes/lens-editor/page-labels/useFetcherWithSnackbar";
import { ScriptSecretContext } from "routes/lens-editor/scripts/ScriptSecretContext";
import ScriptExecuteCaseList from "routes/lens-editor/scripts/ScriptExecuteCaseList";
import { fullName } from "helpers/name-helpers";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { useSnackbar } from "notistack";
import { AddCircle, ArrowDropDown, DataObject, FileDownload, FileUpload } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectClientLenses, selectUserById } from "redux/slices/client";
import ScriptSecretDialog from "./ScriptSecretDialog";
import PSelect from "components/PSelect";
import LoadingPattern from "components/LoadingPattern";
import PasteCasesDialog from "../PasteCasesDialog";
import { AppRoutes } from "AppRoutes";
import { selectLensEditorLens, selectLensEditorLensOutcomeStatusConfiguation } from "redux/slices/lensEditor";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { nonEmpty } from "helpers/coll";
import { atomOneLight, CopyBlock } from "react-code-blocks";
import CheckboxList from "components/CheckboxList";
import { selectFormFields } from "redux/slices/form.slice";
import PRadioGroup from "../../../components/PRadioGroup";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { HelpIconOutlined } from "components/CustomIcons";
import CollapsibleSections from "components/CollapsibleSections";
import ScriptParameters from "routes/lens-editor/scripts/ScriptParameters";
import Papa from "papaparse";
import triggerDownload from "helpers/triggerDownload";
import { ALL_LENS_SCRIPT_FLAGS_VALUES, displayLensScriptFlag } from "./LensScriptFlag";
import PublishScriptModal from "routes/lens-editor/PublishScriptModal";
import { extractGQLError } from "shared/PatternGraphQLError";

export const PopoverCell = ({
  output,
  displayText,
}: {
  output: string | undefined | null;
  displayText?: string | undefined | null;
}) => {
  const showMore = typeof output === "string" && output?.length > 3;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {displayText ? displayText.slice(0, 3) : output?.slice(0, 3)}
        {showMore ? "..." : ""}
        {showMore && (
          <>
            <IconButton size="small" onClick={handleClick}>
              <ArrowDropDown />
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}>
              <Box sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <pre>{output}</pre>
              </Box>
            </Popover>
          </>
        )}
      </Box>
    </TableCell>
  );
};

const ScriptRoute = () => {
  const { lensId } = useParamNumbers(["lensId"]);
  const { scriptName } = useParams();
  const [approvalSecret] = useContext(ScriptSecretContext);
  const script = useScriptNameLoader(scriptName);
  const upsertFetcher = useFetcher();
  const upsertFetcherResponse = useDecoratedFetcher(upsertFetcher);
  const approveFetcher = useFetcher();
  useFetcherWithSnackbar(approveFetcher, "Script approved", "Unable to approve script");
  const { enqueueSnackbar } = useSnackbar();

  if (!script || lensId === undefined) {
    return <Navigate to=".." />;
  }

  const formFields = useAppSelector(selectFormFields);
  const clientLenses = useAppSelector(selectClientLenses);
  const clientLens = clientLenses[lensId];
  const lens = useAppSelector(selectLensEditorLens);
  const scriptUser = useAppSelector((state) => selectUserById(state, script?.updatedBy ? script.updatedBy : 0));
  const [tabValue, setTabValue] = useState<"results" | "failures">("results");
  const casesHandle = useState<SearchCasesResult[]>([]);
  const [stateScriptName, setStateScriptName] = useState(script?.name ?? "");
  const [flags, setFlags] = useState<LensScriptFlag[]>(script?.flags ?? []);
  const [scriptContents, setScriptContents] = useState(script?.scriptContent ?? "");
  const [testResults, setTestResults] = useState<ScriptResultView[]>([]);
  const calcColumns: string[] = useMemo(() => {
    const set = new Set<string>();
    testResults.forEach((tr) => {
      tr.calcs.forEach((c) => {
        set.add(c.key);
      });
    });
    return [...set.keys()].sort();
  }, [testResults]);
  const propertyColumns: string[] = useMemo(() => {
    const set = new Set<string>();
    testResults.forEach((tr) => {
      tr.properties.forEach((p) => set.add(p.key));
    });
    return [...set.keys()];
  }, [testResults]);
  const successes = useMemo(() => testResults.filter((tr) => !tr.failure), [testResults]);
  const failures = useMemo(() => testResults.filter((tr) => !!tr.failure), [testResults]);
  const [scriptTabValue, setScriptTabValue] = useState<"calc-schemas" | "script" | "execute">("script");
  const [scriptSecret] = useContext(ScriptSecretContext);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [scriptParameters, setScriptParameters] = useState<KeyValue[]>([{ key: "", value: "" }]);
  const [defaultScriptParameters, setDefaultScriptParameters] = useState<KeyValue[]>(script.defaultParameters || []);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const submitConfig: Partial<SubmitOptions> = {
    encType: "application/json",
    method: "POST",
    action: `/lens-editor/${lensId}/scripts`,
  };
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [pastedCases, setPastedCases] = useState<number[]>([]);
  const [plaintiffFirmId, setPlaintiffFirmId] = useState<number | undefined>(undefined);
  const firms = useFirmsByLensId(lensId);
  const [runOccurring, setRunOccurring] = useState(false);
  const [caseStatus, setCaseStatus] = useState<CaseClientStatus | undefined>(undefined);
  const [reviewStatus, setReviewStatus] = useState<CaseReviewStatus | undefined>(undefined);
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatus | undefined>(undefined);
  const [requiredFieldFindings, setRequiredFieldFindings] = useState<number[]>(script.requiredFieldFindings);
  const lensOutcomeStatusConfigurations = useAppSelector(selectLensEditorLensOutcomeStatusConfiguation);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const caseIds = [...casesHandle[0].map((c) => c.caseId), ...pastedCases];
  const [isDraggedOn, setIsDraggedOn] = useState(false);
  const anyFiltersUsed: boolean =
    caseStatus !== undefined ||
    reviewStatus !== undefined ||
    outcomeStatus !== undefined ||
    plaintiffFirmId !== undefined;
  const casesSelectedForRun: boolean = nonEmpty(caseIds) || anyFiltersUsed;
  const [async, setAsync] = useState(false);
  const testAndRunDisabled = !stateScriptName || !casesSelectedForRun || !script?.isApproved || runOccurring;
  const runArguments: Pick<
    RunScriptMutationVariables,
    "dryRun" | "lensId" | "scriptName" | "filters" | "scriptParameters" | "async"
  > = {
    lensId,
    scriptName: stateScriptName,
    scriptParameters: scriptParameters.filter((sp) => sp.key !== ""),
    filters: {
      caseIds,
      plaintiffFirmId,
      caseStatus,
      reviewStatus,
    },
    dryRun,
    async,
  };
  const [showPublishScriptModal, setShowPublishScriptModal] = useState(false);
  const _runScript = async () => {
    try {
      setRunOccurring(true);
      const response = await runGqlMutation(RunScriptDocument, runArguments);
      setTestResults(response.lens.scripts.runScript.scriptResults);
    } catch (e) {
      try {
        const message = JSON.parse((e as any)?.message);
        enqueueSnackbar(message.message, { variant: "error" });
      } catch (e) {
        enqueueSnackbar("Error testing script", { variant: "error" });
      }
    } finally {
      setRunOccurring(false);
    }
  };

  const runScript = async () => {
    if (dryRun) {
      await _runScript();
    } else {
      confirmCtx.confirm({
        title: `Are you sure you want to run ${stateScriptName}?`,
        prompt: <P>This will run against all cases matching the filters.</P>,
        callback: _runScript,
      });
    }
  };

  const saveScript = async () => {
    const trimmedDefaultParameters = defaultScriptParameters.filter((sp) => sp.key !== "");
    upsertFetcher.submit(
      {
        intent: "upsert",
        lensId,
        scriptName: stateScriptName,
        scriptContent: scriptContents,
        flags,
        requiredFieldFindings,
        defaultParameters: trimmedDefaultParameters,
      },
      submitConfig
    );
  };

  const approveScript = async () => {
    if (scriptSecret) {
      approveFetcher.submit(
        {
          lensId,
          intent: "approve",
          scriptName: stateScriptName,
          approvalSecret,
        },
        submitConfig
      );
    } else {
      setShowSecretDialog(true);
    }
  };

  const onFileUploadClick = () => {
    if (inputFileRef.current) inputFileRef.current.click();
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = function (evt) {
      setScriptContents(evt.target?.result as string);
      if (inputFileRef.current) {
        inputFileRef.current.value = "";
      }
    };
  };

  const onFile = (evt: ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    handleFile(file);
  };

  // This whole 'useEffect reset() on name change' is bad. What we should do instead is
  // ensure this component is unmounted if the name changes
  const reset = () => {
    setStateScriptName(scriptName || "");
    setScriptContents(script?.scriptContent || "");
    setTestResults([]);
    setFlags(script?.flags ?? []);
    setRequiredFieldFindings(script.requiredFieldFindings || []);
    setDefaultScriptParameters(script.defaultParameters || []);
  };

  useEffect(() => {
    reset();
  }, [scriptName]);

  useEffect(() => {
    if (upsertFetcherResponse.completed) {
      enqueueSnackbar("Script saved", { variant: "success" });
    }
  }, [upsertFetcherResponse.completed]);

  useEffect(() => {
    if (upsertFetcherResponse.errored) {
      try {
        const error = upsertFetcher.data.error;
        const message = JSON.parse(error.message);
        enqueueSnackbar(message.message, { variant: "error" });
      } catch (e) {
        const gql = extractGQLError(e);
        enqueueSnackbar(`Unable to save script: ${gql?.message ?? "Unknown error"}`, { variant: "error" });
      }
    }
  }, [upsertFetcherResponse.errored, upsertFetcher.data]);

  const createResultObjects = (scriptResults: ScriptResultView[]): Record<string, string>[] => {
    return scriptResults.map((s) => {
      const result: Record<string, string> = {};
      result.caseId = s.caseId.toString();
      result.actionTypes = s.actionTypes.join(",");
      calcColumns.forEach((calcColumn) => {
        result[calcColumn] = s.calcs.find((calc) => calc.key === calcColumn)?.value ?? "";
      });
      propertyColumns.forEach((propColumn) => {
        result[propColumn] = s.properties.find((p) => p.key === propColumn)?.value ?? "";
      });
      return result;
    });
  };

  const handleDownloadCSV = () => {
    if (!testResults) {
      return;
    }
    const date = formatDateBackend(new Date());
    const successObjects = createResultObjects(successes);
    const successCsv = Papa.unparse(successObjects, {});
    const successCsvBlob = new Blob([successCsv], { type: "text/csv" });
    triggerDownload(successCsvBlob, `script_results_${lensId}_${date}.csv`);

    if (failures.length !== 0) {
      const failureObjects = createResultObjects(failures);
      const failureCsv = Papa.unparse(failureObjects, {});
      const failureCsvBlob = new Blob([failureCsv], { type: "text/csv" });
      triggerDownload(failureCsvBlob, `script_results_${lensId}_failures_${date}.csv`);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {showSecretDialog && (
        <ScriptSecretDialog
          onSave={() => {
            setShowSecretDialog(false);
            void approveScript();
          }}
          open={showSecretDialog}
          close={() => setShowSecretDialog(false)}
          helperText=""
        />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", mb: 1, height: "100%", minHeight: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <P variant="lgBold">{scriptName}</P>
          <P variant="sm" sx={{ color: theme.palette.grey["400"], ml: 1 }}>
            Last edited by {scriptUser !== undefined ? fullName(scriptUser) : INTERNAL_USER_NAME} at{" "}
            {displayDateAndTime(script?.updatedAt ? new Date(script.updatedAt) : undefined)}
          </P>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<FileUpload />}
            onClick={onFileUploadClick}
            sx={{
              ml: 2,
            }}>
            Select File
            <input
              ref={inputFileRef}
              type="file"
              autoComplete="off"
              tabIndex={-1}
              style={{ display: "none" }}
              onChange={onFile}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleDownloadCSV}
            disabled={testResults?.length === 0}
            sx={{
              ml: 2,
            }}>
            Download
          </Button>
          {lens.isFamilyPrimary && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setShowPublishScriptModal(true)}
              sx={{
                ml: 2,
              }}>
              Publish to Lens Family
            </Button>
          )}
          {showPublishScriptModal && scriptName && (
            <PublishScriptModal
              lensId={lensId}
              scriptName={scriptName}
              open={true}
              close={() => setShowPublishScriptModal(false)}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />
          <FormGroup>
            <FormControlLabel
              control={
                <Switch disabled={script?.isApproved} onChange={approveScript} checked={script?.isApproved ?? false} />
              }
              label="Approved"
            />
          </FormGroup>
        </Box>
        <Tabs value={scriptTabValue} onChange={(_evt, tab) => setScriptTabValue(tab)}>
          <Tab label="Edit" value="script" />
          <Tab label="Execute" value="execute" />
        </Tabs>
        {scriptTabValue === "execute" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "300px 1fr", flexGrow: 1, height: "100%", minHeight: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1, minHeight: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <P variant="baseSemiBold" sx={{ mr: 1 }}>
                  Cases
                </P>
                <PRadioGroup
                  row
                  options={["Hand-picked", "Using Filters"]}
                  value={filtersExpanded ? "Using Filters" : "Hand-picked"}
                  onChange={(value) => setFiltersExpanded(value === "Using Filters")}
                  control={<Radio size="small" />}
                />
              </Box>

              {!filtersExpanded && (
                <>
                  <ScriptExecuteCaseList
                    label="Pick Cases"
                    casesHandle={casesHandle}
                    lensId={lensId}
                    scriptName={stateScriptName}
                  />
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
                    <P variant="xs">Or paste</P>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPasteDialogOpen(true)}
                      disabled={nonEmpty(casesHandle[0])}>
                      Paste Pattern Case Ids ({pastedCases.length})
                    </Button>
                  </Box>
                </>
              )}
              {pasteDialogOpen && (
                <PasteCasesDialog
                  open={pasteDialogOpen}
                  onConfirm={(caseIds) => {
                    setPasteDialogOpen(false);
                    setPastedCases(caseIds);
                  }}
                  onCancel={() => {
                    setPasteDialogOpen(false);
                  }}
                />
              )}
              {filtersExpanded && (
                <>
                  <FormControl size="small">
                    <InputLabel variant="outlined">Firm</InputLabel>
                    <PSelect
                      label="Firm"
                      value={plaintiffFirmId || ""}
                      onChange={(evt) => {
                        const v = evt.target.value as string;
                        setPlaintiffFirmId(parseSafeInt(v));
                      }}>
                      <MenuItem value="">Show All</MenuItem>
                      {firms.map((firm) => (
                        <MenuItem key={firm.id} value={firm.id.toString()}>
                          {firm.name}
                        </MenuItem>
                      ))}
                    </PSelect>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel variant="outlined">Case Status</InputLabel>
                    <PSelect
                      label="Case Status"
                      value={caseStatus || ""}
                      onChange={(evt) => {
                        const v = evt.target.value as CaseClientStatus;
                        setCaseStatus(v || undefined);
                      }}>
                      <MenuItem value="">Show All</MenuItem>
                      <MenuItem value={CaseClientStatus.Active}>
                        Active {clientLens?.casesOverview ? `(${clientLens.casesOverview.activeCount || 0})` : ""}
                      </MenuItem>
                      <MenuItem value={CaseClientStatus.Inactive}>
                        Inactive {clientLens?.casesOverview ? `(${clientLens.casesOverview.inactiveCount || 0})` : ""}
                      </MenuItem>
                    </PSelect>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel variant="outlined">Review Status</InputLabel>
                    <PSelect
                      label="Review Status"
                      value={reviewStatus || ""}
                      onChange={(evt) => {
                        const v = evt.target.value as CaseReviewStatus;
                        // Check for Show All being selected, if so, clear out all review statuses
                        setReviewStatus(v || undefined);
                      }}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="complete">
                        Complete {clientLens?.casesOverview ? `(${clientLens.casesOverview.completeCount || 0})` : ""}
                      </MenuItem>
                      <MenuItem value="new">
                        Pending {clientLens?.casesOverview ? `(${clientLens.casesOverview.newCount || 0})` : ""}
                      </MenuItem>
                      <MenuItem value="new_documents">
                        Pending - New Documents{" "}
                        {clientLens?.casesOverview ? `(${clientLens.casesOverview.newDocumentsCount || 0})` : ""}
                      </MenuItem>
                      <MenuItem value="no_documents">
                        No Documents{" "}
                        {clientLens?.casesOverview ? `(${clientLens.casesOverview.noDocumentsCount || 0})` : ""}
                      </MenuItem>
                    </PSelect>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel variant="outlined">Outcome Status</InputLabel>
                    <PSelect
                      label="Outcome Status"
                      value={outcomeStatus || ""}
                      onChange={(evt) => {
                        const v = evt.target.value as OutcomeStatus;
                        setOutcomeStatus(v || undefined);
                      }}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value={OutcomeStatus.DiscrepantInformation}>
                        {displayOutcomeStatus(
                          OutcomeStatus.DiscrepantInformation,
                          undefined,
                          lensOutcomeStatusConfigurations
                        )}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.MissingInformation}>
                        {displayOutcomeStatus(
                          OutcomeStatus.MissingInformation,
                          undefined,
                          lensOutcomeStatusConfigurations
                        )}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.NotApplicable}>
                        {displayOutcomeStatus(OutcomeStatus.NotApplicable, undefined, lensOutcomeStatusConfigurations)}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.PendingRecords}>
                        {displayOutcomeStatus(OutcomeStatus.PendingRecords, undefined, lensOutcomeStatusConfigurations)}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.Rejected}>
                        {displayOutcomeStatus(OutcomeStatus.Rejected, undefined, lensOutcomeStatusConfigurations)}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.Unreviewed}>
                        {displayOutcomeStatus(OutcomeStatus.Unreviewed, undefined, lensOutcomeStatusConfigurations)}
                      </MenuItem>
                      <MenuItem value={OutcomeStatus.Valid}>
                        {displayOutcomeStatus(OutcomeStatus.Valid, undefined, lensOutcomeStatusConfigurations)}
                      </MenuItem>
                    </PSelect>
                  </FormControl>
                </>
              )}
              <Divider />
              <P variant="baseSemiBold">Script Parameters</P>
              <ScriptParameters scriptParameters={scriptParameters} setScriptParameters={setScriptParameters} />
              <Button
                onClick={() => setScriptParameters((prev) => [...prev, { key: "", value: "" }])}
                size="small"
                startIcon={<AddCircle />}
                variant="outlined">
                Add Parameter
              </Button>
              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={(evt) => setDryRun(evt.target.checked)} />}
                label="Dry Run"
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}>
                <FormControlLabel
                  control={<Checkbox checked={async} onChange={(evt) => setAsync(evt.target.checked)} />}
                  label="Async"
                />
                <Tooltip title="Execute asynchronously. Use for large runs (>100). Results will not be available in this UI; see history table for results.">
                  <HelpIconOutlined fontSize="small" />
                </Tooltip>
              </Box>

              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button disabled={testAndRunDisabled} onClick={runScript} variant="contained">
                  Run
                </Button>
              </Box>
            </Box>
            <Box sx={{ pl: 2, height: "100%", display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
              {runOccurring && <LoadingPattern centered />}
              {testResults.length > 0 && !runOccurring && (
                <>
                  <Tabs value={tabValue} onChange={(_evt, newValue) => setTabValue(newValue)}>
                    <Tab label={`Results (${successes.length})`} value="results" />
                    <Tab label={`Failures (${failures.length})`} value="failures" />
                  </Tabs>

                  {tabValue === "results" && (
                    <TableContainer sx={{ height: "100%" }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Case Id</TableCell>
                            <TableCell>Inputs</TableCell>
                            <TableCell>Action Types</TableCell>
                            <TableCell>Console</TableCell>
                            {calcColumns.map((c) => (
                              <TableCell key={c}>{c}</TableCell>
                            ))}
                            {propertyColumns.map((p) => (
                              <TableCell key={p}>{p}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {successes.map((tr, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Link to={AppRoutes.caseDetailBuilder(lensId, tr.caseId)} target="_blank">
                                    {tr.caseId}
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex" }}>
                                    <CopyToClipboard text={`${tr.inputMapGroovy}`} title="Copy Input to Clipboard" />
                                    <CopyToClipboard
                                      text={`${tr.testCaseJson}`}
                                      copyIcon={<DataObject />}
                                      title="Copy JSON to Clipboard"
                                    />
                                  </Box>
                                </TableCell>
                                <PopoverCell output={tr.actionTypes.join("\n")} displayText={tr.actionTypes.at(0)} />
                                <PopoverCell output={tr.stdOutput} />
                                {calcColumns.map((calcKey) => {
                                  const calc = tr.calcs.find((c) => c.key === calcKey);
                                  return (
                                    <TableCell title={calc?.value ?? ""} key={calcKey}>
                                      {calc?.value}
                                    </TableCell>
                                  );
                                })}
                                {propertyColumns.map((propertyKey) => {
                                  const property = tr.properties.find((p) => p.key === propertyKey);
                                  return (
                                    <TableCell title={property?.value ?? ""} key={propertyKey}>
                                      {property?.value}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {tabValue === "failures" && (
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Case Id</TableCell>
                            <TableCell>Inputs</TableCell>
                            <TableCell>Failure Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {failures.map((tr, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell>{tr.caseId}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex" }}>
                                    <CopyToClipboard text={`${tr.inputMapGroovy}`} title="Copy Input to Clipboard" />
                                    <CopyToClipboard
                                      text={`${tr.testCaseJson}`}
                                      copyIcon={<DataObject />}
                                      title="Copy JSON to Clipboard"
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>{tr.failure}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 400px",
              minHeight: 0,
              gap: 1,
              pt: 2,
              pr: 1,
              height: "100%",
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                overflow: "auto",
                fontFamily: "Roboto mono; monospace",
                border: isDraggedOn ? `2px dashed ${theme.palette.primary.main}` : undefined,
              }}
              onDrop={(evt: DragEvent<HTMLDivElement>) => {
                evt.preventDefault();
                evt.stopPropagation();

                if (evt.dataTransfer.files.length > 0) {
                  const file = evt.dataTransfer.files.item(0);
                  if (file !== null) {
                    handleFile(file);
                  }
                }
                setIsDraggedOn(false);
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();

                setIsDraggedOn(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();

                setIsDraggedOn(false);
              }}
              onDragEnd={(event) => {
                event.preventDefault();
                event.stopPropagation();

                setIsDraggedOn(false);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggedOn(true);
              }}>
              {!scriptContents ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 1,
                  }}>
                  <P variant="xlBold">Empty Script</P>
                  <P variant="sm">Drop a file here or click the button above to upload a script</P>
                </Box>
              ) : (
                <CopyBlock
                  customStyle={{ fontSize: "10px" }}
                  text={scriptContents}
                  language="groovy"
                  showLineNumbers
                  theme={atomOneLight}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                py: 1,
                px: 1,
                borderRadius: `${theme.shape.borderRadius}px`,
                minHeight: 0,
              }}>
              <Box
                sx={{
                  overflow: "auto",
                  flex: 1,
                  gap: 1,
                  display: "flex",
                  flexDirection: "column",
                }}>
                <CollapsibleSections
                  sections={[
                    {
                      title: <P variant="lgBold">Flags</P>,
                      disabled: true,
                      items: [
                        <FormGroup key="flags" sx={{ px: 1.5 }}>
                          {ALL_LENS_SCRIPT_FLAGS_VALUES.map((flag) => {
                            return (
                              <FormControlLabel
                                key={flag}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={flags.includes(flag)}
                                    onChange={(evt) => {
                                      if (evt.target.checked) {
                                        setFlags((prev) => [...prev, flag]);
                                      } else {
                                        setFlags((prev) => prev.filter((f) => f !== flag));
                                      }
                                    }}
                                  />
                                }
                                label={displayLensScriptFlag(flag as LensScriptFlag)}
                              />
                            );
                          })}
                        </FormGroup>,
                      ],
                      expanded: true,
                    },
                  ]}
                />
                <CollapsibleSections
                  sections={[
                    {
                      title: <P variant="lgBold">Required Field Findings</P>,
                      items: [
                        <CheckboxList
                          key="required-field-findings"
                          allItems={formFields.map((f) => f.id)}
                          itemKey={(fieldId) => fieldId.toString()}
                          itemName={(fieldId) => {
                            const f = formFields.find((f) => f.id === fieldId);
                            return f ? `${f.outlinePrefix} ${f.name}` : "Unknown";
                          }}
                          onChange={(fieldIds) => setRequiredFieldFindings(fieldIds)}
                          selected={requiredFieldFindings}
                        />,
                      ],
                      expanded: true,
                    },
                  ]}
                />

                <CollapsibleSections
                  sections={[
                    {
                      title: <P variant="lgBold">Default Parameters</P>,
                      items: [
                        <Box key="default-parameters" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <ScriptParameters
                            scriptParameters={defaultScriptParameters}
                            setScriptParameters={setDefaultScriptParameters}
                          />
                          <Button
                            onClick={() => setDefaultScriptParameters((prev) => [...prev, { key: "", value: "" }])}
                            size="small"
                            startIcon={<AddCircle />}
                            variant="outlined">
                            Add Parameter
                          </Button>
                        </Box>,
                      ],
                      expanded: true,
                    },
                  ]}
                />
              </Box>
              <Button onClick={saveScript} disabled={!stateScriptName} variant="contained">
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScriptRoute;
